import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'
import { COMPARE_PAGES } from '@/lib/compare-index'
import { breadcrumbList, itemList, jsonLdScript, webPage } from '@/lib/schema'

const URL = 'https://uptrack.app/compare'
const TITLE = `Compare Uptrack with ${COMPARE_PAGES.length} Uptime Monitoring Tools | Uptrack`
const DESCRIPTION = `Side-by-side comparisons of Uptrack with ${COMPARE_PAGES.length} other uptime monitoring tools: plans, check intervals, status pages and alerts.`

export const Route = createFileRoute('/_site/compare/')({
  component: CompareIndexPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:image', content: 'https://uptrack.app/og/home.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      jsonLdScript(webPage({ name: TITLE, description: DESCRIPTION, url: URL, type: 'CollectionPage' })),
      jsonLdScript(breadcrumbList([{ name: 'Home', url: 'https://uptrack.app' }, { name: 'Compare', url: URL }])),
      jsonLdScript(
        itemList(
          'Uptrack comparisons',
          COMPARE_PAGES.map((p) => ({ name: `Uptrack vs ${p.name}`, url: `https://uptrack.app${p.href}` })),
          URL,
        ),
      ),
    ],
  }),
})

function CompareIndexPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-12 md:py-16">
          <p className="num mb-4 text-sm text-muted-foreground">{COMPARE_PAGES.length} tools compared</p>
          <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Uptrack compared with other monitoring tools</h1>
          <p className="mt-5 max-w-2xl text-lead text-body">
            Each page puts Uptrack next to one other tool: plans, check intervals, status pages and how each one confirms downtime. The facts come from each tool&apos;s public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.
          </p>
          <p className="mt-4 text-body">
            Looking for a list instead? See the{' '}
            <Link to="/alternatives" className="font-medium text-primary underline-offset-4 hover:underline">
              alternatives to each tool
            </Link>
            .
          </p>
          <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COMPARE_PAGES.map((p) => (
              <li key={p.slug}>
                <Link
                  to={p.href}
                  className="group flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
                >
                  <span className="text-[17px] font-semibold text-foreground group-hover:text-primary">Uptrack vs {p.name}</span>
                  <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-y border-border bg-card">
        <div className="container mx-auto grid grid-cols-1 items-center gap-6 px-4 py-12 md:py-[72px] lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Try Uptrack</h2>
            <p className="mt-3 max-w-xl text-lead text-body">Choose Pro, Team, or Business for 30-second monitoring and alerts.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
              <StartFreeButton />
              <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">See plans</Link>
              </Button>
            </div>
            <PlanFinePrint className="mt-3" />
          </div>
        </div>
      </section>
    </>
  )
}
